import React from "react";
import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { mediaMax } from "components/designV2/breakpoints";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityLogo from "images/icons/svg/v2/simflexity-logo.svg";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "components/buttons/buttons.styled";
import { useAuthStore } from "stores/authStore/authStore";
import { CompanyRole } from "model/User";

export interface MenuItem {
  value: string;
  label: string;
  iconName: string;
  href: string;
  badgeCount?: number;
}

interface MenuProps {
  items: MenuItem[];
  activeValue: string;
  logoSrc?: string;
  onItemClick?: (
    item: MenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => void;
}

/**
 * Top navigation menu with logo and selectable items.
 */
export const NavbarMenu = ({
  items,
  activeValue,
  logoSrc = SimflexityLogo,
  onItemClick,
}: MenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const [authState, _authActions] = useAuthStore();

  const handleItemClick = (
    item: MenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ): void => {
    onItemClick?.(item, event);
  };

  return (
    <Container role="navigation" aria-label={t("DesignComponents.MainMenu")}>
      {authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/schedule">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      {authState.user?.role == CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/stampclock">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      <Items>
        {items.map((item) => {
          const isActive = item.value === activeValue;
          return (
            <ItemLink
              key={item.value}
              to={item.href}
              aria-current={isActive ? "page" : undefined}
              $isActive={isActive}
              onClick={(event) => handleItemClick(item, event)}
            >
              <IconWrap>
                <CustomIcon
                  name={item.iconName}
                  size="24px"
                  color={isActive ? ColorV2.Blue1 : ColorV2.Blue5}
                />
                {typeof item.badgeCount === "number" && item.badgeCount > 0 && (
                  <Badge>{item.badgeCount}</Badge>
                )}
              </IconWrap>
              <Label $isActive={isActive}>{item.label}</Label>
            </ItemLink>
          );
        })}
      </Items>
    </Container>
  );
};

const Container = styled.nav`
  align-items: center;
  background-color: ${ColorV2.White};
  border-radius: 24px;
  display: flex;
  flex: 1;
  height: 64px;
  min-width: 0;
  padding: 0 clamp(12px, 2vw, 24px);
  overflow-x: scroll;
`;

const Logo = styled.img`
  height: 64px;
  object-fit: contain;
  width: 140px;

  ${mediaMax.desktop} {
    width: 100px;
  }
`;

const Items = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: clamp(16px, 4vw, 64px);
  justify-content: flex-end;

  ${mediaMax.desktop} {
    justify-content: flex-start;
    gap: 20px;
    margin-left: 16px;
  }
`;

const ItemLink = styled(Link)<{ $isActive: boolean }>`
  ${TypographyV2.b1}
  align-items: center;
  color: ${(props) => (props.$isActive ? ColorV2.Blue1 : ColorV2.Blue5)};
  display: inline-flex;
  gap: ${(props) => (props.$isActive ? "12px" : "0")};
  text-decoration: none;
  transition:
    gap 0.2s ease,
    color 0.2s ease;

  &:hover {
    color: ${ColorV2.Blue1};
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  position: relative;
`;

const Label = styled.span<{ $isActive: boolean }>`
  ${TypographyV2.b1}
  color: inherit;
  max-width: ${(props) => (props.$isActive ? "180px" : "0")};
  opacity: ${(props) => (props.$isActive ? 1 : 0)};
  overflow: hidden;
  transform: ${(props) =>
    props.$isActive ? "translateX(0)" : "translateX(-6px)"};
  transition:
    max-width 0.2s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
  white-space: nowrap;
`;
